import { err, ok, Result } from "neverthrow";
import GraphqlApiError from "@/framework/network/gql/GraphqlApiError";
import { GraphqlApi, IGraphqlApi } from "@/framework/network/gql/GraphqlApi";
import { SignInToken, SignInTokenSchema } from "@/modules/auth/objects/SignInToken";
import { GeneralResult, GeneralResultSchema } from "@/framework/objects/GeneralResult";
import { SandboxCount, SandboxCountSchema, SandboxInfo, SandboxInfoSchema } from "@/modules/admin/objects/sandbox";
import {
  ApplySandboxDocument,
  ApplySandboxMutation,
  CheckSandboxExistingDocument,
  CheckSandboxExistingQuery,
  CreateSandboxDocument,
  CreateSandboxMutation,
  DeleteSandboxDocument,
  DeleteSandboxMutation,
  ExchangeAccountWithSandboxDocument,
  ExchangeAccountWithSandboxMutation,
  GetSandboxInfoDocument,
  GetSandboxInfoQuery,
  UpdateSandboxInfoDocument,
  UpdateSandboxInfoMutation,
} from "@/framework/network/gql/generated/graphql";

export class SandboxService {
  private _graphql: IGraphqlApi;

  public constructor(graphql: IGraphqlApi = new GraphqlApi()) {
    this._graphql = graphql;
  }

  async getSandbox(isSandbox: boolean): Promise<Result<SandboxInfo, GraphqlApiError>> {
    const res = await this._graphql.runQueryAndValidate<GetSandboxInfoQuery, SandboxInfo>(
      GetSandboxInfoDocument,
      SandboxInfoSchema,
      { isSandbox },
      true
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async exchangeAccount(): Promise<Result<SignInToken, GraphqlApiError>> {
    const res = await this._graphql.runMutationAndValidate<ExchangeAccountWithSandboxMutation, SignInToken>(
      // @ts-ignore conflict between generic typing and exact requirement
      ExchangeAccountWithSandboxDocument,
      SignInTokenSchema,
      {}
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async updateSandboxLogoutNote(note: string): Promise<Result<SignInToken, GraphqlApiError>> {
    const res = await this._graphql.runMutationAndValidate<UpdateSandboxInfoMutation, SignInToken>(
      UpdateSandboxInfoDocument,
      SignInTokenSchema,
      { note }
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async createSandboxDraft(): Promise<Result<GeneralResult, GraphqlApiError>> {
    const res = await this._graphql.runMutationAndValidate<CreateSandboxMutation, GeneralResult>(
      // @ts-ignore conflict between generic typing and exact requirement
      CreateSandboxDocument,
      GeneralResultSchema,
      {}
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async applySandboxDraft(): Promise<Result<GeneralResult, GraphqlApiError>> {
    const res = await this._graphql.runMutationAndValidate<ApplySandboxMutation, GeneralResult>(
      // @ts-ignore conflict between generic typing and exact requirement
      ApplySandboxDocument,
      GeneralResultSchema,
      {}
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async deleteSandboxDraft(isSandbox: boolean): Promise<Result<GeneralResult, GraphqlApiError>> {
    const res = await this._graphql.runMutationAndValidate<DeleteSandboxMutation, GeneralResult>(
      DeleteSandboxDocument,
      GeneralResultSchema,
      { isSandbox }
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async checkSandboxExists(): Promise<Result<SandboxCount, GraphqlApiError>> {
    const res = await this._graphql.runQueryAndValidate<CheckSandboxExistingQuery, SandboxCount>(
      // @ts-ignore conflict between generic typing and exact requirement
      CheckSandboxExistingDocument,
      SandboxCountSchema,
      {}
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }
}
